import React, {useEffect, useRef, useState} from "react";
import {Button, Card, Col, Row, Space, Spin, Switch, Table} from "antd";
import {FormattedMessage, useIntl} from "react-intl";
import {MedicalStaff} from "../api-client";
import DetailWrapper from "../components/common/DetailWrapper";
import {Link, useNavigate} from "react-router-dom";
import {isMobileOnly} from "react-device-detect";
import {PlusOutlined} from "@ant-design/icons";
import SearchForm from "../components/common/SearchForm";
import InfiniteScroll from 'react-infinite-scroll-component';

import UserIcon from "../components/common/UserIcon";
import {accessibilityRoles} from "../utils/accessUtils";
import AccessControl from "../components/common/AccessControl";
import {getMedicalStaffApi} from "../api/apiInstances";
import logger from "../utils/logger";

const MedicalStaffComp = () => {

    const navigate = useNavigate();
    const intl = useIntl();

    const [keyword, setKeyword] = useState<string>('');
    const [sortBy, setSortBy] = useState<('id' | 'name')>('id');
    const [page, setPage] = useState<number>(1);
    const [height, setHeight] = useState<number>(window.innerHeight);
    const [showInactive, setShowInactive] = useState<boolean>(false);

    const [medicalStaff, setMedicalStaff] = useState<MedicalStaff[]>([]);
    const [initialLoading, setInitialLoading] = useState<boolean>(true);
    const [totalElements, setTotalElements] = useState<number>(0);
    const loadingRef = useRef<boolean>(false);
    const medicalStaffApi = getMedicalStaffApi();

    useEffect(() => {
        setHeight(window.innerHeight * 0.01);
        const handleResize = () => {
            setHeight(window.innerHeight * 0.01);
        };
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        loadData(true, sortBy, keyword, showInactive);
    }, []);

    const loadData = (refresh: boolean, sortBy: 'id' | 'name', keyword: string, includeInactive: boolean) => {
        if (loadingRef.current) return;

        loadingRef.current = true;

        if (refresh) {
            setPage(1);
        } else {
            setPage(prevState => prevState + 1);
        }

        medicalStaffApi.fetchAllMedicalStaff(keyword, sortBy || 'id', refresh ? 1 : page + 1, 6, includeInactive)
            .then((response) => {
                logger.log(response);

                if (refresh) {
                    setMedicalStaff(response.data.medicalStaff!);
                } else {
                    setMedicalStaff(prevStaff => prevStaff.concat(response.data.medicalStaff!));
                }

                setTotalElements(response.data.totalElements!);
                setInitialLoading(false);
                loadingRef.current = false;
            })
            .catch(error => {
                logger.log("Error loading medical staff:", error);
                setInitialLoading(false);
                loadingRef.current = false;
            });
    }

    const handleToggleInactive = (checked: boolean) => {
        setShowInactive(checked);
        loadData(true, sortBy, keyword, checked);
    };

    return (
        <AccessControl
            accessibleFor={[accessibilityRoles.CLINIC_ADMIN, accessibilityRoles.RECEPTIONIST]}>
            <DetailWrapper navigateBack={undefined} removeVerticalScroll={true}>
                <Row className="mb-2">
                    <Col span={12}>
                        <h3 style={{width: '140%'}}><FormattedMessage id="medical_staff"/></h3>
                    </Col>
                    <Col span={12}>
                        <Link style={{float: 'right', marginTop: '24px'}} to={"/medical/staff/create"}>
                            <div className={isMobileOnly ? "mobile-btn-wrapper" : ""}>
                                <Button shape="round"
                                        className={isMobileOnly ? "mobile-add-btn" : "dentobook-btn-primary dentobook-btn-medium"}
                                        type="primary"><PlusOutlined/> <FormattedMessage id="add"/></Button>
                            </div>
                        </Link>
                    </Col>
                </Row>

                <Row className="mb-2">
                    <Col>
                        <Space>
                            <Switch
                                checked={showInactive}
                                onChange={handleToggleInactive}
                            />
                            <span className="body-large-regular">
                                <FormattedMessage id="show_inactive"/>
                            </span>
                        </Space>
                    </Col>
                </Row>

                <SearchForm entity="medical" keyword={keyword} setKeyword={setKeyword} sortBy={sortBy}
                            setSortBy={setSortBy}
                            loadData={(refresh: boolean, sortBy: 'id' | 'name', keyword: string) => loadData(refresh, sortBy, keyword, showInactive)}/>
                {initialLoading ? (
                    <div style={{ textAlign: 'center', padding: '100px 0' }}>
                        <Spin tip={<FormattedMessage id="loading" />} />
                    </div>
                ) : medicalStaff.length === 0 ? (
                    <b className="text-center"><FormattedMessage id="no_data"/></b>
                ) : (
                    <div id="scrollableDiv" style={{
                        height: `${height * 80 - (!isMobileOnly ? 170 : 300)}px`,
                        width: isMobileOnly ? '90%' : '97%',
                        overflow: 'auto',
                        position: 'absolute'
                    }}>
                        <InfiniteScroll
                            scrollableTarget="scrollableDiv"
                            dataLength={medicalStaff.length}
                            next={() => loadData(false, sortBy, keyword, showInactive)}
                            hasMore={medicalStaff.length < totalElements}
                            loader={<span />}
                            style={{overflow: 'hidden'}}
                            refreshFunction={() => loadData(true, sortBy, keyword, showInactive)}
                            pullDownToRefresh
                        >
                            {isMobileOnly ?
                                <Table dataSource={medicalStaff}
                                       pagination={false}
                                       className="dentobook-table"
                                       onRow={(person) => {
                                           return {
                                               onClick: () => {
                                                   navigate('/medical/staff/' + person.id);
                                               }
                                           };
                                       }}>
                                    <Table.Column key="name"
                                                  render={(person) => (
                                                      <span style={person.isActive === false ? {opacity: 0.45} : {}}>
                                                          {person.firstName + ' ' + person.lastName}
                                                      </span>
                                                  )}
                                                  title={<FormattedMessage id="name"/>}
                                    />
                                    <Table.Column key="role"
                                                  render={(person) => (
                                                      <span style={person.isActive === false ? {opacity: 0.45} : {}}>
                                                          {person.role ? <FormattedMessage id={person.role}/> : '-'}
                                                      </span>
                                                  )}
                                                  title={<FormattedMessage id="role"/>}
                                    />
                                </Table>
                                :
                                <Row gutter={24}>
                                    {medicalStaff.map(person => (
                                        <Col lg={8} key={person.id}>
                                            <Card className="medical-staff-card"
                                                  style={person.isActive === false ? {opacity: 0.45} : {}}
                                                  onClick={() => navigate('/medical/staff/' + person.id)}>
                                                <Space direction="vertical" className="medical-staff-container">
                                                    <div className="mb-4">
                                                        {!person.avatarUrl ? <UserIcon size="medium" rounded={true}/> :
                                                            <img className="photo-img-medical-staff"
                                                                 width={60}
                                                                 src={person.avatarUrl}
                                                                 alt="avatar"/>
                                                        }
                                                    </div>
                                                    <span className="font-weight-500 font-16 font-family-roboto">
                                                    {person.firstName + " " + (person.secondName || "") + " " + person.lastName}
                                                </span>
                                                    <span className="font-weight-400 font-14 font-family-roboto">
                                                    {person.role ? <FormattedMessage id={person.role}/> : '-'}
                                                </span>
                                                </Space>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            }
                        </InfiniteScroll>
                    </div>
                )}
            </DetailWrapper>
        </AccessControl>
    )
};

export default MedicalStaffComp;
